import React, { useState, useEffect } from 'react'
import {
  makeStyles,
  Container,
  Box,
  Typography,
} from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { useLocation, useHistory } from 'react-router-dom'
import { useQuery } from 'react-query'
import teamStatisticsPageStyles from './teamStatisticsPage.styles'
import statsTableStyles from './statsTable.styles'
import ContainerPadding from '../../components/containerPadding/containerPadding'
import getPlayer from '../../Apis/player/getPlayer'
import getPlayerStats from '../../Apis/stats/getPlayerStats'
import getPlayerCareerStats from '../../Apis/stats/getPlayerCareerStats'
import getPlayerSeasonStats from '../../Apis/stats/getPlayerSeasonStats'
import QueryKeys from '../../utils/constants/queryKeys'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import PlayerInfoCard from './PlayerInfoCard'
import PlayerStatsTable from './PlayerStatsTable'
import PlayerSeasonSummaryTable from './PlayerSeasonSummaryTable'
import PlayerCompetitionStatsTable from './PlayerCompetitionStatsTable'
import PlayerGameLogTable from './PlayerGameLogTable'
import StatsGrid from './StatsGrid'
import SeasonDetailDialog from './SeasonDetailDialog'
import MyToggleButton from '../../components/MyToggleButton/MyToggleButton'
import { useCmsScope } from '../../context/useCmsScope'
import useUrlSearchParams from '../../hooks/useUrlSearchParams'
import ORGANISATION_TYPES from '../../utils/constants/OrganisationTypes'
import isSelectedOrgCompetitionOrFederation from '../../utils/helpers/isSelectedOrgCompetitionOrFederation'

const MODE_THIS_SEASON = 'this-season'
const MODE_ALL_TIME = 'all-time'

const gridFromAverages = (a = {}) => ({
  avgPoints: a.points !== undefined ? Number(a.points).toFixed(1) : '-',
  avgRebounds: a.totalRebounds !== undefined ? Number(a.totalRebounds).toFixed(1) : '-',
  avgAssists: a.assists !== undefined ? Number(a.assists).toFixed(1) : '-',
  avgSteals: a.steals !== undefined ? Number(a.steals).toFixed(1) : '-',
  avgBlocksAgainst: a.blocks !== undefined ? Number(a.blocks).toFixed(1) : '-',
  avgPir: a.pir !== undefined ? Number(a.pir).toFixed(1) : '-',
})

// ── Federation view (This season / All time) ─────────────────────────────────

const FederationPlayerStats = ({ playerId, orgId, readOnly, classes, statsClasses }) => {
  const [mode, setMode] = useState(MODE_THIS_SEASON)
  const [selectedStageId, setSelectedStageId] = useState(null)
  const [selectedSeason, setSelectedSeason] = useState(null)
  const [selectedCompetitionName, setSelectedCompetitionName] = useState(null)

  // A paired club reads only its paired seasons: the server refuses a career (it spans the others),
  // and a refused read would retry through token refreshes before the page showed anything.
  const { data: careerStatsData, isFetching: isCareerStatsFetching } = useQuery(
    [QueryKeys.GET_PLAYER_CAREER_STATS, orgId, playerId],
    () => getPlayerCareerStats({ orgId, playerId }),
    { enabled: !readOnly && Boolean(orgId) && Boolean(playerId) },
  )

  const careerStats = careerStatsData?.data
  const competitions = careerStats?.competitions || []
  const careerAverages = careerStats?.careerAverages
  const careerDisplayStats = careerStats?.displayStats

  // Current-season stats (server defaults to most recent when seasonId is omitted).
  // Includes per-stage totals/averages/games used by the stage filter below. A paired club
  // may be paired into several seasons, so it reads the one it is browsing when the URL names it.
  const { seasonId: browsedSeasonId } = useUrlSearchParams(['seasonId']) || {}
  const seasonId = (readOnly && browsedSeasonId) || undefined
  const { data: seasonStatsData, isFetching: isSeasonStatsFetching } = useQuery(
    [QueryKeys.GET_PLAYER_SEASON_STATS, orgId, playerId, seasonId],
    () => getPlayerSeasonStats({ orgId, playerId, seasonId }),
    { enabled: mode === MODE_THIS_SEASON && Boolean(orgId) && Boolean(playerId) },
  )

  const seasonStats = seasonStatsData?.data
  const stages = seasonStats?.stages || []
  const seasonDisplayStats = seasonStats?.displayStats

  // Default to the first stage as soon as season data arrives.
  useEffect(() => {
    if (stages.length > 0 && !stages.some(s => s.stageId === selectedStageId)) {
      setSelectedStageId(stages[0].stageId)
    }
  }, [stages, selectedStageId])

  const selectedStage = stages.find(s => s.stageId === selectedStageId) || null
  const displayTotals = selectedStage?.totals || seasonStats?.totals
  const displayAverages = selectedStage?.averages || seasonStats?.averages
  const displayGames = selectedStage?.games || []

  if (isCareerStatsFetching) {
    return (
      <ContainerPadding paddingValue='45%'>
        <LoaderDots className={classes.loaderDots} />
      </ContainerPadding>
    )
  }

  if (competitions.length === 0 && !seasonStats && !isSeasonStatsFetching) {
    return (
      <Box textAlign='center' padding='40px 20px'>
        <Typography variant='h6' color='textSecondary'>
          No career statistics available
        </Typography>
      </Box>
    )
  }

  const isThisSeason = mode === MODE_THIS_SEASON

  return (
    <ContainerPadding paddingValue='1%'>
      {!readOnly && (
        <Box marginBottom='16px'>
          <MyToggleButton
            left={{ value: MODE_THIS_SEASON, label: 'This season' }}
            right={{ value: MODE_ALL_TIME, label: 'All time' }}
            toggleValue={mode}
            setToggleValue={setMode}
          />
        </Box>
      )}

      <Box marginBottom='24px'>
        <StatsGrid
          stats={gridFromAverages(isThisSeason ? (displayAverages || {}) : careerAverages)}
          showValue='average'
          displayStats={isThisSeason ? seasonDisplayStats : careerDisplayStats}
        />
      </Box>

      {isThisSeason ? (
        isSeasonStatsFetching ? (
          <Box textAlign='center' padding='20px'>
            <LoaderDots className={classes.loaderDots} />
          </Box>
        ) : !seasonStats ? (
          <Box textAlign='center' padding='40px 20px'>
            <Typography variant='h6' color='textSecondary'>
              No season statistics available
            </Typography>
          </Box>
        ) : (
          <>
            <Typography className={statsClasses.sectionHeading}>
              {seasonStats.seasonName} season stats
            </Typography>
            <PlayerSeasonSummaryTable
              totals={displayTotals}
              averages={displayAverages}
              displayStats={seasonDisplayStats}
            />

            <Box className={statsClasses.gameStatsHeaderRow}>
              <Typography className={statsClasses.sectionHeading}>
                Game stats
              </Typography>
              {stages.length > 0 && (
                <ToggleButtonGroup
                  value={selectedStageId}
                  exclusive
                  size='small'
                  onChange={(_, value) => { if (value) setSelectedStageId(value) }}
                  className={statsClasses.stageToggle}
                >
                  {stages.map(stage => (
                    <ToggleButton
                      key={stage.stageId}
                      value={stage.stageId}
                      classes={{
                        root: statsClasses.stageToggleButton,
                        selected: statsClasses.stageToggleButtonSelected,
                      }}
                    >
                      {stage.stageName}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            </Box>

            {displayGames.length === 0 ? (
              <Box textAlign='center' padding='20px'>
                <Typography color='textSecondary'>
                  No games for this stage
                </Typography>
              </Box>
            ) : (
              <PlayerGameLogTable games={displayGames} displayStats={seasonDisplayStats} />
            )}
          </>
        )
      ) : (
        competitions.map(comp => (
          <React.Fragment key={comp.competitionId}>
            <Typography className={statsClasses.sectionHeading}>
              {comp.competitionName}
            </Typography>
            <PlayerCompetitionStatsTable
              competition={comp}
              onSeasonClick={(season) => {
                setSelectedSeason(season)
                setSelectedCompetitionName(comp.competitionName)
              }}
              displayStats={careerDisplayStats}
            />
          </React.Fragment>
        ))
      )}

      <SeasonDetailDialog
        open={Boolean(selectedSeason)}
        onClose={() => setSelectedSeason(null)}
        playerId={playerId}
        orgId={orgId}
        season={selectedSeason}
        competitionName={selectedCompetitionName}
      />
    </ContainerPadding>
  )
}

// ── Default view (existing per-season/per-game stats) ─────────────────────────

const DefaultPlayerStats = ({ playerId, orgId, playerData, classes }) => {
  const location = useLocation()
  const history = useHistory()
  const { data: playerStats, isFetching: isPlayerStatsFetching } = useQuery(
    [QueryKeys.GET_PLAYER_STATS, orgId, playerId],
    () => getPlayerStats({ orgId, playerId }),
    { enabled: Boolean(orgId) && Boolean(playerId) },
  )

  const teamId = playerData?.teams[0]?.team

  useEffect(() => {
    if (teamId && !isSelectedOrgCompetitionOrFederation()) {
      const searchParams = new URLSearchParams(location.search)
      searchParams.set('team', teamId)
      history.replace(`${location.pathname}?${searchParams.toString()}`)
    }
  }, [teamId])

  if (isPlayerStatsFetching) {
    return (
      <ContainerPadding paddingValue='45%'>
        <LoaderDots className={classes.loaderDots} />
      </ContainerPadding>
    )
  }

  return (
    <ContainerPadding paddingValue='1%'>
      <StatsGrid
        stats={playerStats?.data.playerStatsDto.stats}
        displayStats={playerStats?.data?.playerStatsDto?.displayStats}
      />
      <PlayerStatsTable
        playerData={playerStats?.data}
        displayStats={playerStats?.data?.playerStatsDto?.displayStats}
      />
    </ContainerPadding>
  )
}

// ── Page shell ────────────────────────────────────────────────────────────────

const PlayerStatisticsPage = () => {
  const useStyles = makeStyles(teamStatisticsPageStyles)
  const useStatsTableStyles = makeStyles(statsTableStyles)
  const classes = useStyles()
  const statsClasses = useStatsTableStyles()
  const location = useLocation()

  const playerId = location?.pathname?.split('/').filter(Boolean).pop()
  const { orgId, orgType, readOnly } = useCmsScope()
  const isFederation = orgType === ORGANISATION_TYPES.ORG_FEDERATION

  // A paired club reads another org's player, so under that org's route rather than the header.
  const { data: playerData, isFetching: isPlayerDataFetching } = useQuery(
    [QueryKeys.GET_PLAYER, playerId, readOnly && orgId],
    () => getPlayer({ playerId, orgId: readOnly ? orgId : undefined }),
    { enabled: Boolean(playerId) },
  )

  return (
    <Container>
      <ContainerPadding paddingValue='1%' className={classes.statsContainer}>
        {isPlayerDataFetching ? (
          <ContainerPadding paddingValue='45%'>
            <LoaderDots className={classes.loaderDots} />
          </ContainerPadding>
        ) : (
          <ContainerPadding paddingValue='1%'>
            <PlayerInfoCard playerInfo={playerData} />
          </ContainerPadding>
        )}

        {isFederation ? (
          <FederationPlayerStats
            playerId={playerId}
            orgId={orgId}
            readOnly={readOnly}
            classes={classes}
            statsClasses={statsClasses}
          />
        ) : (
          <DefaultPlayerStats
            playerId={playerId}
            orgId={orgId}
            playerData={playerData}
            classes={classes}
          />
        )}
      </ContainerPadding>
    </Container>
  )
}

export default PlayerStatisticsPage
